import { Component, OnInit } from '@angular/core';
import { PoModule, PoNotificationService, PoTableColumn, PoTableLiterals } from '@po-ui/ng-components';
import { ApiService } from '../../services/api.service';
import { LocalStorageType } from '../../types/localStorage.type';
import { downloadFile } from '../../utils/download.utils';
import { SessionService } from '../../services/session.service';
import { Router } from '@angular/router';
import { clearSessionData } from '../../utils/clearSessionData.util';
import { TenantService } from '../../services/tenant.service';
import { CommonModule } from '@angular/common';
import { registerLocaleData } from '@angular/common';
import ptBr from '@angular/common/locales/pt';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';

@Component({
  standalone: true,
  imports: [PoModule, CommonModule, PaginationComponent],
  selector: 'invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.css']
})
export class InvoicesComponent implements OnInit {

  loadingSize: string = 'md';
  loadingText: string = '';
  isLoadingHidden = true;
  isMobile: boolean = window.innerWidth <= 768;
  isHiddenPersonName: boolean = true;
  currentPage: number = 1;
  isDisableShowMore: boolean = false;
  totalPages: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;

  constructor(
    private apiService: ApiService,
    private poNotification: PoNotificationService,
    private sessionService: SessionService,
    private router: Router,
    private tenantService: TenantService
  ) {
    registerLocaleData(ptBr, 'pt-BR');
  }

  async ngOnInit(): Promise<void> {
    await this.searchInvoices();
  }

  async searchInvoices() {
    if (!this.onValidToken())
      return;

    this.isLoadingHidden = false;

    await this.apiService.setterHeader('PTCAPP002');
    let branchCodeList: string[] = [];
    const storageBranches = JSON.parse(localStorage.getItem(LocalStorageType.Branches) || '[]');
    branchCodeList = storageBranches.map((item: any) => {
      const key = Object.keys(item)[0];
      return item[key].branchCode.toString();
    });

    const body = {
      page: this.currentPage,
      pageSize: this.pageSize,      
      order: "-invoiceDate, branchCode"
    };

    this.apiService.postInvoicesSearch(body).subscribe(
      (response) => {
        const storageBranches = JSON.parse(
          localStorage.getItem(LocalStorageType.Branches) || '[]',
        );

        this.totalPages = response.totalPages;
        this.totalItems = response.count;

        const novosItensMapeados = response.items.map((item: any) => {
          const branch = storageBranches.find(
            (branchItem: any) => branchItem[item.branchCode],
          );

          var personName = item.personName || '';
          if (this.isHiddenPersonName === true && personName.length > 0) {
            this.isHiddenPersonName = false;
            this.columns = this.columns.map(column => {
              if (column.property === 'personName') {
                return { ...column, visible: true };
              }
              return column;
            });
          }

          return {
            branchCode: item.branchCode,
            fantasyName: branch ? branch[item.branchCode].fantasyName : null,
            personName: item.personName,
            invoiceCode: item.invoiceCode,
            serialCode: item.serialCode,
            issueDate: item.issueDate,
            documentType: this.descFiscalDocument(item.documentType),
            accessKey: item.accessKey,
            totalValue: item.totalValue,
            invoice: {
              branchCode: item.branchCode,
              invoiceSequence: item.invoiceSequence,
              invoiceDate: item.invoiceDate
            },
            iconXml: 'xml',
            iconDanfe: item.documentType !== 59 && item.documentType !== 65 ? 'danfe' : null,
          };
        });
        this.items = novosItensMapeados;
        this.isLoadingHidden = true;
      },
      (error) => {
        if (error.error && Array.isArray(error.error) && error.error.length > 0) {
          const errorMessage = error.error[0].message || 'Erro desconhecido.';
          this.poNotification.error(errorMessage);
        } else if (error.message) {
          this.poNotification.error(error.message);
        } else {
          this.poNotification.error('Ocorreu um erro desconhecido.');
        }
        this.isLoadingHidden = true;
      },
    );
  }

  title = "Notas fiscais"

  items!: Array<any>;

  columns: Array<PoTableColumn> = [
    { property: 'branchCode', label: 'Código', type: 'number', visible: false },
    { property: 'fantasyName', label: 'Empresa', type: 'string', visible: false },
    { property: 'personName', label: 'Nome', type: 'string', visible: false },
    { property: 'invoiceCode', label: 'Nota fiscal', type: 'number' },
    { property: 'serialCode', label: 'Série NF', type: 'string' },
    { property: 'issueDate', label: 'Data de emissão', type: 'date' },
    { property: 'documentType', label: 'Modelo NF', type: 'string' },
    { property: 'accessKey', label: 'Chave de acesso', type: 'string' },
    { property: 'totalValue', label: 'Total da nota', type: 'currency', format: 'BRL' },
    {
      property: 'iconXml', label: 'XML', type: 'icon', icons: [
        { value: 'xml', icon: 'ph ph-file-xml', color: 'color-01', tooltip: 'Baixar XML', action: this.downloadXml.bind(this) }
      ]
    },
    {
      property: 'iconDanfe', label: 'DANFE', type: 'icon', icons: [
        { value: 'danfe', icon: 'ph ph-file-pdf', color: 'color-01', tooltip: 'Baixar PDF DANFE', action: this.downloadDanfe.bind(this) }
      ]
    }
  ]

  downloadXml(item: any) {
    if (!this.onValidToken())
      return;

    this.apiService.postAction("OBTER_XML").subscribe();
    this.isLoadingHidden = false;
    this.apiService.getXmlContents(item.accessKey).subscribe(
      response => {
        this.downloadInvoice(item.accessKey, response.mainInvoiceXml, 'xml')
        this.isLoadingHidden = true;
      },
      error => {
        if (error.error && Array.isArray(error.error) && error.error.length > 0) {
          const errorMessage = error.error[0].message || 'Erro desconhecido.';
          this.poNotification.error(errorMessage);
        } else if (error.message) {
          this.poNotification.error(error.message);
        } else {
          this.poNotification.error('Ocorreu um erro ao gerar o XML.');
        }
        this.isLoadingHidden = true;
      });
  }

  downloadDanfe(item: any) {
    if (!this.onValidToken())
      return;

    this.apiService.postAction("OBTER_DANFE").subscribe();
    this.isLoadingHidden = false;
    const body = item.invoice;

    this.apiService.postDanfe(body).subscribe(
      response => {
        this.downloadInvoice(item.accessKey, response.content, 'pdf')
        this.isLoadingHidden = true;
      },
      error => {
        if (error.error && Array.isArray(error.error) && error.error.length > 0) {
          const errorMessage = error.error[0].message || 'Erro desconhecido.';
          this.poNotification.error(errorMessage);
        } else if (error.message) {
          this.poNotification.error(error.message);
        } else {
          this.poNotification.error('Ocorreu um erro ao gerar o PDF da DANFE.');
        }
        this.isLoadingHidden = true;
      });
  }

  private downloadInvoice(accessKey: string, base64String: any, extension: string) {
    downloadFile(base64String, accessKey, extension)
  }

  customLiterals: PoTableLiterals = {
    noData: ''
  };

  private descFiscalDocument(code: number): string {
    switch (code) {
      case 55:
      case 85:
      case 86:
        return 'NF-e';
      case 65:
        return 'NFC-e';
      case 59:
        return 'S@T';
      default:
        return 'Modelo desconhecido';
    }
  }

  private onValidToken() {
    if (this.sessionService.isTokenExpiredOrNull().hasAccess !== true) {
      clearSessionData();
      this.router.navigate(['/login'], { queryParams: { tenant: this.tenantService.getTenant() } });
      return false
    }

    return true
  }

  showMore() {
    this.currentPage++;
    this.searchInvoices();
  }

  onPageChange(newPage: number, pageSize: number) {
    this.currentPage = newPage;
    this.pageSize = pageSize;

    this.searchInvoices();
  }

}
